/**
 * TelemetryTransport — ships MechanicalTelemetry batches off the device.
 *
 * Preferred path: a WebSocket to the telemetry endpoint (dev: /ws/telemetry served by the
 * Vite plugin; prod: VITE_TELEMETRY_WS). Fallback: POST /api/telemetry. Batches are queued
 * and flushed on a short debounce so several games' 3s snapshots coalesce into one frame;
 * final batches (exit conditions) flush immediately, and pagehide uses keepalive POSTs.
 */
import { entryContext } from '../lib/share';
import { nextVisitOrdinal, visitId } from '../lib/visit';
import { ensureSession, supabase, supabaseConfigured } from '../backend/supabase';
import type { MechanicalTelemetry } from './TelemetryTypes';
import { postTelemetry } from '../api/mockApi';

export type TransportMode = 'websocket' | 'http';

export interface TransportStatus {
  mode: TransportMode;
  wsState: 'disabled' | 'connecting' | 'open' | 'closed' | 'failed';
  sentBatches: number;
  sentMessages: number;
  failed: number;
  queued: number;
  lastAckAt: number;
  endpoint: string;
}

export interface TransportOptions {
  /** The client's persistent local id; rides on every row as client_mint_id. */
  clientMintId?: string;
  /**
   * Feed index of a card right now. Asked once per session, at its first batch, and stamped
   * as feed_position on every row of that session — so a session that started at slot 3
   * stays "slot 3" even after someone links a card in front of it.
   */
  positionOf?: (cardId: string) => number | undefined;
  /**
   * performance.now() at the first 'running' card of this visit, once known (App records it).
   * Stamped as boot_ms on rows whose visit_ordinal is 1; every other row goes without.
   */
  bootMs?: () => number | undefined;
  wsUrl?: string | null;
  flushDebounceMs?: number;
  maxRetries?: number;
  onStatus?: (s: TransportStatus) => void;
}

export function resolveWsUrl(): string | null {
  const env = (import.meta.env.VITE_TELEMETRY_WS as string | undefined)?.trim();
  if (env === 'off' || env === 'none' || env === 'false') return null;
  if (env && env !== 'auto') return env;
  if (import.meta.env.DEV || env === 'auto') {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/ws/telemetry`;
  }
  return null;
}

export class TelemetryTransport {
  private queue: MechanicalTelemetry[] = [];
  private ws: WebSocket | null = null;
  private retries = 0;
  private flushTimer = 0;
  private status: TransportStatus;
  private readonly opts: Required<Pick<TransportOptions, 'flushDebounceMs' | 'maxRetries'>> & TransportOptions;
  readonly log: MechanicalTelemetry[] = [];

  constructor(opts: TransportOptions = {}) {
    this.opts = { flushDebounceMs: 150, maxRetries: 4, ...opts };
    const wsUrl = opts.wsUrl === undefined ? resolveWsUrl() : opts.wsUrl;
    this.status = {
      mode: wsUrl ? 'websocket' : 'http',
      wsState: wsUrl ? 'connecting' : 'disabled',
      sentBatches: 0,
      sentMessages: 0,
      failed: 0,
      queued: 0,
      lastAckAt: 0,
      endpoint: wsUrl ?? '/api/telemetry',
    };
    if (wsUrl) this.connect(wsUrl);
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flush(true));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
    }
  }

  getStatus(): TransportStatus {
    return { ...this.status, queued: this.queue.length };
  }

  private emitStatus() {
    this.opts.onStatus?.(this.getStatus());
  }

  private connect(url: string) {
    try {
      const ws = new WebSocket(url);
      this.ws = ws;
      this.status.wsState = 'connecting';
      ws.onopen = () => {
        this.status.wsState = 'open';
        this.status.mode = 'websocket';
        this.retries = 0;
        this.emitStatus();
        if (this.queue.length) this.flush();
      };
      ws.onmessage = () => {
        this.status.lastAckAt = Date.now();
        this.emitStatus();
      };
      ws.onclose = () => {
        this.ws = null;
        this.status.wsState = 'closed';
        if (this.retries < this.opts.maxRetries) {
          const delay = 500 * 2 ** this.retries++;
          setTimeout(() => this.connect(url), delay);
        } else {
          this.status.wsState = 'failed';
          this.status.mode = 'http';
        }
        this.emitStatus();
      };
      ws.onerror = () => {
        /* onclose follows */
      };
    } catch {
      this.status.wsState = 'failed';
      this.status.mode = 'http';
      this.emitStatus();
    }
  }

  /** Per-session stamps, fixed at the session's first batch. Bounded; old sessions fall off. */
  private sessionStamps = new Map<string, { feed_position?: number; visit_ordinal: number }>();

  private stampsFor(b: MechanicalTelemetry): Pick<MechanicalTelemetry, 'feed_position' | 'visit_ordinal' | 'visit_id'> {
    let s = this.sessionStamps.get(b.session_uuid);
    if (!s) {
      const pos = this.opts.positionOf?.(b.game_id);
      s = { visit_ordinal: nextVisitOrdinal(), ...(Number.isInteger(pos) && (pos as number) >= 0 ? { feed_position: pos } : {}) };
      this.sessionStamps.set(b.session_uuid, s);
      if (this.sessionStamps.size > 500) this.sessionStamps.delete(this.sessionStamps.keys().next().value as string);
    }
    const boot = s.visit_ordinal === 1 ? this.opts.bootMs?.() : undefined;
    return { ...s, visit_id: visitId(), ...(boot !== undefined ? { boot_ms: Math.round(boot) } : {}) };
  }

  send(incoming: MechanicalTelemetry) {
    // Session-level attribution is stamped here, in the one place every host's rows pass
    // through, so a wasm worker and a web card cannot disagree about who referred the session.
    const batch: MechanicalTelemetry = {
      ...incoming,
      ...entryContext(),
      client_mint_id: this.opts.clientMintId,
      ...this.stampsFor(incoming),
    };
    this.queue.push(batch);
    this.log.push(batch);
    if (this.log.length > 200) this.log.shift();
    if (batch.is_final) {
      this.flush();
    } else if (!this.flushTimer) {
      this.flushTimer = window.setTimeout(() => this.flush(), this.opts.flushDebounceMs);
    }
    this.emitStatus();
  }

  flush(keepalive = false) {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = 0;
    }
    if (!this.queue.length) return;
    const batches = this.queue;
    this.queue = [];

    if (supabaseConfigured && supabase) {
      const client = supabase;
      void ensureSession().then(async (session) => {
        if (!session) {
          this.queue.unshift(...batches); // no session yet: keep them, try on the next flush
          this.emitStatus();
          return;
        }
        const { data, error } = await client.rpc('ingest_batch', { batches });
        if (error) {
          // Rate limited or transient: re-queue. A malformed batch is counted by the RPC as
          // rejected rather than failing the call, so nothing here loops forever.
          this.queue.unshift(...batches);
          this.status.failed += batches.length;
        } else {
          const row = Array.isArray(data) ? data[0] : data;
          this.status.sentBatches += Number(row?.accepted ?? batches.length);
          this.status.sentMessages++;
        }
        this.emitStatus();
      });
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN && !keepalive) {
      try {
        this.ws.send(JSON.stringify(batches));
        this.status.sentBatches += batches.length;
        this.status.sentMessages++;
        this.emitStatus();
        return;
      } catch {
        /* fall through to HTTP */
      }
    }
    void postTelemetry(batches, keepalive).then((ok) => {
      if (ok) {
        this.status.sentBatches += batches.length;
        this.status.sentMessages++;
      } else {
        this.status.failed += batches.length;
      }
      this.emitStatus();
    });
  }
}
